import { useEffect, useState } from 'react';
import { UseFormReturn, useWatch, Controller } from 'react-hook-form';
import { Label } from '../../../components/common/FormLabel';
import { ErrorLabel } from '../../../components/common/ErrorLabel';
import { Input } from '../../../components/common/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/shadcn/select';
import { Checkbox } from '../../../components/shadcn/checkbox';
import { Button } from '../../../components/shadcn/button';
import DropdownMultiSelect from '../../../components/common/Dropdown/DropdownMultiSelect';
import { SelectOption } from '../../../components/common/Dropdown/types';
import { getLibraryItemCreateViewModel, getLibraryItemEditViewModel } from '../../../api/libraryItem/api';
import { CrmItemViewModel, DisplayStyleType, PipelineReportDisplayBy, FilterViewModel, FilterType, DynamicFilterViewModel, PipelineReportColumn, DateFilterMode, AvailablePipelineStages } from '../../../types/domain';
import { DynamicFiltersSection } from './DynamicFiltersSection';
import { LoadingOverlay } from '@/components/common/Loading/LoadingOverlay';
import { LoadingPage } from '@/components/common/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlus } from '@fortawesome/free-solid-svg-icons';

type CrmLibraryItemFormProps = {
  formMethods: UseFormReturn<CrmItemViewModel>;
  hasValidationError?: boolean;
  onValidationErrorHandled?: () => void;
  pipelineId?: string | null;
  libraryItemId?: string | null;
  groupId?: string | null;
  onOptionsChange: (options: {
    groupOptions: SelectOption[];
    visibleColumnsOptions: SelectOption[];
    sortColumnOptions: SelectOption[];
    displayByOptions: SelectOption[];
    libraryItemTypeOptions: SelectOption[];
    displayStyleOptions: SelectOption[];
    dateFilterModeOptions: SelectOption[];
    availableFilters: FilterViewModel[];
    pipelineOptions: SelectOption[];
    multiSelectSelectedOptions: Record<string, SelectOption[]>;
    availablePipelineStages: SelectOption[];
  }) => void;
  onCancel?: () => void;
};

export const CrmLibraryItemForm = ({ formMethods, hasValidationError, onValidationErrorHandled, pipelineId, libraryItemId, groupId, onOptionsChange, onCancel }: CrmLibraryItemFormProps) => {
  const { register, control, formState: { errors } } = formMethods;

  const [isLoading, setIsLoading] = useState(true);

  const visibleColumns = (useWatch({ control: formMethods.control, name: 'visibleColumns' }) as string[] | undefined) ?? [];
  const displayBy = useWatch({ control: formMethods.control, name: 'displayBy' }) as string | undefined;
  const displayStyle = useWatch({ control: formMethods.control, name: 'displayStyle' }) as string | undefined;
  const sortColumn = useWatch({ control: formMethods.control, name: 'sortColumn' }) as string | undefined;
  const dateFilterMode = useWatch({ control: formMethods.control, name: 'dateFilterMode' }) as string | undefined;
  const selectedPipelineId = useWatch({ control: formMethods.control, name: 'pipelineId' }) as string | undefined;

  const [availablePipelines, setAvailablePipelines] = useState<SelectOption[]>([]);
  const [availableCrmTypes, setAvailableCrmTypes] = useState<SelectOption[]>([]);
  const [availableColumns, setAvailableColumns] = useState<SelectOption[]>([]);
  const [availableGroups, setAvailableGroups] = useState<SelectOption[]>([]);
  const [availableDisplayBy, setAvailableDisplayBy] = useState<SelectOption[]>([]);
  const [availableDateFilterModes, setAvailableDateFilterModes] = useState<SelectOption[]>([]);
  const [availableFilters, setAvailableFilters] = useState<FilterViewModel[]>([]);
  const [allAvailablePipelineStages, setAllAvailablePipelineStages] = useState<AvailablePipelineStages[]>([]);
  const [filteredPipelineStages, setFilteredPipelineStages] = useState<SelectOption[]>([]);
  const [previousPipelineId, setPreviousPipelineId] = useState<string | null>(null);

  // Local state for multiselect selectedOptions
  const [multiSelectSelectedOptions, setMultiSelectSelectedOptions] = useState<Record<string, SelectOption[]>>({});

  // Callback to handle multiselect selectedOptions changes
  const handleMultiSelectSelectedOptionsChange = (filterId: string, selectedOptions: SelectOption[]) => {
    setMultiSelectSelectedOptions(prev => ({
      ...prev,
      [filterId]: selectedOptions
    }));
  };

  const getDisplayByOptions = () => {
    if (availableDisplayBy && availableDisplayBy.length > 0) {
      return availableDisplayBy.map(opt => ({ value: opt.value, label: opt.label }));
    }
    return [];
  };

  // Theme - only visible when display style is funnel
  const getThemeOptions = () => {
    return [
      { value: 'prgn', label: 'Purple-Green' },
      { value: 'category10', label: 'Category' },
      { value: 'spectral', label: 'Spectral' },
      { value: 'tableau10', label: 'Tableau' },
      { value: 'paired', label: 'Paired' },
      { value: 'accent', label: 'Accent' },
      { value: 'blues', label: 'Blues' },
      { value: 'greens', label: 'Greens' },
      { value: 'reds', label: 'Reds' },
      { value: 'purples', label: 'Purples' },
      { value: 'oranges', label: 'Oranges' },

      { value: 'rdylbu', label: 'Red-Yellow-Blue' },
      { value: 'rdylgn', label: 'Red-Yellow-Green' },
      { value: 'rdbu', label: 'Red-Blue' },
      { value: 'rdgy', label: 'Red-Grey' },
      { value: 'piyg', label: 'Pink-Yellow-Green' },
      { value: 'purd', label: 'Purple-Red' },
      { value: 'pubugn', label: 'Purple-Blue-Green' },
      { value: 'pubu', label: 'Purple-Blue' },
      { value: 'ylorbr', label: 'Yellow-Orange-Brown' },
      { value: 'ylord', label: 'Yellow-Orange-Red' },
      { value: 'ylgnbu', label: 'Yellow-Green-Blue' },
      { value: 'ylgn', label: 'Yellow-Green' },
    ];
  };

  // Display Style options - funnel only available when display by stage
  // TODO: these rules should be passed from the BFF
  const getDisplayStyleOptions = () => {
    const options = [
      { value: DisplayStyleType[DisplayStyleType.dsGrid], label: 'Grid' }
    ];
    if (displayBy === PipelineReportDisplayBy[PipelineReportDisplayBy.Stage]) {
      options.push({ value: DisplayStyleType[DisplayStyleType.dsFunnelChart], label: 'Funnel' });
    }
    return options;
  };

  // Sort By options - only visible when display style is grid
  // TODO: these rules should be passed from the BFF
  const getSortColumnOptions = () => {
    if (displayStyle !== DisplayStyleType[DisplayStyleType.dsGrid]) {
      return [];
    }

    const options = [];

    if (typeof displayBy !== 'undefined') {
      switch (displayBy) {
        case PipelineReportDisplayBy[PipelineReportDisplayBy.Company]:
          options.push({ value: PipelineReportColumn[PipelineReportColumn.Company], label: 'Company' });
          break;
        case PipelineReportDisplayBy[PipelineReportDisplayBy.User]:
          options.push({ value: PipelineReportColumn[PipelineReportColumn.User], label: 'User' });
          break;
        case PipelineReportDisplayBy[PipelineReportDisplayBy.Stage]:
          options.push({ value: PipelineReportColumn[PipelineReportColumn.Stage], label: 'Stage' });
          break;
        case PipelineReportDisplayBy[PipelineReportDisplayBy.Opportunity]:
          options.push({ value: PipelineReportColumn[PipelineReportColumn.Opportunity], label: 'Opportunity' });
          break;
      }
    }

    visibleColumns.forEach((field: string) => {
      const column = availableColumns.find(o => o.value === field);
      if (column) {
        options.push({ value: column.value, label: column.label });
      }
    });
    return options;
  };

  // Get explanation text for Save Dates As option
  const generateDateExplanation = (value: string | undefined): string => {
    if (typeof value === 'undefined' || value === '') return '';

    switch (value) {
      case DateFilterMode[DateFilterMode.Fixed]:
        return 'Use the specific dates in the filter, at fixed points in time.';
      case DateFilterMode[DateFilterMode.RollingDay]:
        return 'Take the dates from the filter and roll them forward every day.';
      case DateFilterMode[DateFilterMode.RollingWeek]:
        return 'Take the dates from the filter and convert them into weeks. Roll these weeks forward each week.';
      case DateFilterMode[DateFilterMode.RollingMonth]:
        return 'Take the dates from the filter and convert them into months. Roll these months forward each month.';
      case DateFilterMode[DateFilterMode.RollingYear]:
        return 'Take the dates from the filter and convert them into years. Roll these years forward each year.';
      default:
        return '';
    }
  };

  // Notify parent when dropdown options change
  // used so the preview can access the currently selected option labels
  // without this the preview only has access to the values e.g. Guids
  useEffect(() => {
    onOptionsChange({
      groupOptions: availableGroups,
      visibleColumnsOptions: availableColumns,
      sortColumnOptions: getSortColumnOptions(),
      displayByOptions: getDisplayByOptions(),
      libraryItemTypeOptions: availableCrmTypes,
      displayStyleOptions: getDisplayStyleOptions(),
      dateFilterModeOptions: availableDateFilterModes,
      availableFilters: availableFilters,
      pipelineOptions: availablePipelines,
      multiSelectSelectedOptions: multiSelectSelectedOptions,
      availablePipelineStages: filteredPipelineStages
    });
  }, [availableGroups, availableColumns, displayBy, displayStyle, visibleColumns, availableDisplayBy, availableCrmTypes, availableDateFilterModes, availableFilters, availablePipelines, multiSelectSelectedOptions, filteredPipelineStages]);

  // Auto-set sortColumn to first available option when columns are unselected and current sortColumn is no longer valid
  useEffect(() => {
    if (displayStyle === DisplayStyleType[DisplayStyleType.dsGrid]) {
      const availableOptions = getSortColumnOptions();
      if (availableOptions.length > 0) {
        // If no sortColumn is selected or current sortColumn is not in available options
        if (!sortColumn || !availableOptions.some(option => option.value === sortColumn)) {
          formMethods.setValue('sortColumn', availableOptions[0].value);
        }
      }
    }
  }, [displayStyle, displayBy, visibleColumns, sortColumn]);

  // Auto-set displayStyle to first available option when current displayStyle is no longer valid due to displayBy change
  useEffect(() => {
    const availableDisplayStyleOptions = getDisplayStyleOptions();
    if (availableDisplayStyleOptions.length > 0) {
      // If no displayStyle is selected or current displayStyle is not in available options
      if (!displayStyle || !availableDisplayStyleOptions.some(option => option.value === displayStyle)) {
        formMethods.setValue('displayStyle', availableDisplayStyleOptions[0].value);
      }
    }
  }, [displayBy, displayStyle]);

  // Filter pipeline stages based on selected pipeline
  useEffect(() => {
    if (selectedPipelineId && allAvailablePipelineStages.length > 0) {
      const pipelineGroup = allAvailablePipelineStages.find(group =>
        group.pipelineId === selectedPipelineId
      );
      const stages = pipelineGroup?.pipelineStages || [];
      setFilteredPipelineStages(stages);

      const firstLoad = previousPipelineId === null;
      const pipelineChanged = previousPipelineId !== selectedPipelineId;

      // Auto-select all stages if pipeline has changed (not on initial load)
      if (!firstLoad && pipelineChanged) {
        const allStageIds = stages.map(stage => stage.value);
        formMethods.setValue('pipelineStages', allStageIds);
      }

      setPreviousPipelineId(selectedPipelineId);
    }
  }, [selectedPipelineId]);

  const loadPage = async () => {
    try {
      setIsLoading(true);

      // TODO: this could be bound using defaultValues, instead of using setValue repeatedly
      const vm: CrmItemViewModel = libraryItemId
        ? await getLibraryItemEditViewModel(libraryItemId)
        : await getLibraryItemCreateViewModel(pipelineId || undefined);

      const groupOptions = vm.availableGroups;

      setAvailablePipelines(vm.availablePipelines);
      setAvailableCrmTypes(vm.availableCrmTypes);
      setAvailableDisplayBy(vm.availableDisplayBy);
      setAvailableDateFilterModes(vm.availableDateFilterModes);
      setAvailableGroups(groupOptions);
      setAvailableFilters(vm.availableFilters || []);
      setAvailableColumns(vm.availableColumns);
      setAllAvailablePipelineStages(vm.availablePipelineStages || []);

      // Populate form with server values
      if (vm.pipelineId) formMethods.setValue('pipelineId', vm.pipelineId);
      if (vm.pipelineStages) formMethods.setValue('pipelineStages', vm.pipelineStages);
      if (vm.libraryItemType) formMethods.setValue('libraryItemType', vm.libraryItemType);
      if (vm.name) formMethods.setValue('name', vm.name);

      //groupId from URL (only if creating new item from home dashboard)
      if (groupId && !libraryItemId) {
        const matchingGroup = groupOptions.find(option => option.value === groupId);
        if (matchingGroup && !matchingGroup.disabled) {
          formMethods.setValue('group', groupId);
        }
      } else if (vm.group) {
        formMethods.setValue('group', vm.group);
      }

      if (typeof vm.currentUserOnly !== 'undefined') formMethods.setValue('currentUserOnly', vm.currentUserOnly);
      if (vm.dateFilterMode) formMethods.setValue('dateFilterMode', vm.dateFilterMode);
      if (vm.displayBy) formMethods.setValue('displayBy', vm.displayBy);
      if (vm.displayStyle) formMethods.setValue('displayStyle', vm.displayStyle);
      if (typeof vm.noResult !== 'undefined') formMethods.setValue('noResult', vm.noResult);
      if (vm.visibleColumns) formMethods.setValue('visibleColumns', vm.visibleColumns);
      if (vm.sortColumn) formMethods.setValue('sortColumn', vm.sortColumn);
      if (typeof vm.sortDesc !== 'undefined') formMethods.setValue('sortDesc', vm.sortDesc);

      if (vm.theme) {
        formMethods.setValue('theme', vm.theme);
      } else {
        formMethods.setValue('theme', getThemeOptions()[0].value);
      }

      // Dynamic Filters
      if (vm.dynamicFilters && Array.isArray(vm.dynamicFilters)) {
        const mapped = vm.dynamicFilters.map((df: DynamicFilterViewModel) => {
          const selectedValues = Array.isArray(df.values) ? df.values : [];
          if (selectedValues.length > 0) {
            const filterConfig = (vm.availableFilters || []).find((f: any) => f.id === df.filterId);
            if (filterConfig) {
              //populate selectedOptions with labels
              let selectedOptions = selectedValues.map((value: string) => {
                const option = filterConfig.values?.find((opt: any) => opt.value.toLowerCase() === value.toLowerCase());
                return {
                  value: value,
                  label: option ? option.label : value
                };
              });

              if (filterConfig.filterType === FilterType.MultiSelect) {
                handleMultiSelectSelectedOptionsChange(df.filterId, selectedOptions);
              }

              return {
                filterId: df.filterId,
                values: selectedValues,
                selectedOptions: selectedOptions
              };
            }
          }
          return {
            filterId: df.filterId,
            values: []
          };
        });
        formMethods.setValue('dynamicFilters', mapped);
      }

    } catch (err) {
      console.error("Error fetching viewmodel: ", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPage();
  }, []);

  if (isLoading) {
    return (
      <>
        <LoadingPage message="Loading library item..." />
        <LoadingOverlay
          isVisible={true}
          message={"Loading library item..."}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Library Item Type */}
      <div>
        <Label forField="libraryItemType" label={<>Library Item Type <span className="text-red-500">*</span></>} />
        <ErrorLabel error={errors.libraryItemType?.message} />
        <Controller
          name="libraryItemType"
          control={control}
          rules={{ required: 'Library item type is required' }}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value || ''}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select library item type" />
              </SelectTrigger>
              <SelectContent>
                {availableCrmTypes.map((option: SelectOption) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Pipeline and Pipeline Stages - Side by side on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pipeline */}
        <div>
          <Label forField="pipelineId" label={<>Pipeline <span className="text-red-500">*</span></>} />
          <ErrorLabel error={errors.pipelineId?.message} />
          <Controller
            name="pipelineId"
            control={control}
            rules={{ required: 'Pipeline is required' }}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={field.value || ''}
              >
                <SelectTrigger
                  className="h-[44px]"
                >
                  <SelectValue placeholder="Select pipeline" />
                </SelectTrigger>
                <SelectContent>
                  {availablePipelines.map((option: SelectOption) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Pipeline Stages */}
        {selectedPipelineId && filteredPipelineStages.length > 0 && (
          <div>
            <Label forField="pipelineStages" label={<>Pipeline Stages <span className="text-red-500">*</span></>} />
            <ErrorLabel error={errors.pipelineStages?.message} />
            <Controller
              name="pipelineStages"
              control={control}
              defaultValue={[]}
              rules={{ required: 'Pipeline stages are required' }}
              render={({ field }) => (
                <DropdownMultiSelect
                  allOptionInResults={false}
                  includeAllOption={true}
                  options={filteredPipelineStages}
                  selected={field.value ? filteredPipelineStages.filter(stage =>
                    field.value.includes(stage.value)
                  ) : []}
                  onChange={(selectedOptions: SelectOption[]) => {
                    field.onChange(selectedOptions.map(option => option.value));
                  }}
                  placeholder="Select pipeline stages..."
                  selectedOptionsToShow={1}
                  inErrorState={!!errors.pipelineStages}
                />
              )}
            />
          </div>
        )}
      </div>

      {/* Name */}
      <div>
        <Label forField="name" label={<>Name <span className="text-red-500">*</span></>} />
        <ErrorLabel error={errors.name?.message} />
        <Input
          inErrorState={!!errors.name}
          {...register('name', { required: 'Name is required', maxLength: { value: 100, message: 'Name must be 100 characters or less' } })}
          maxLength={100}
          placeholder="Enter library item name"
        />
      </div>

      {/* Group */}
      <div>
        <Label forField="group" label={<>Group <span className="text-red-500">*</span></>} />
        <ErrorLabel error={errors.group?.message} />
        <p className="text-sm text-gray-500 mt-1 mb-3">
          Which dashboard group would you like to add this library item to?
        </p>
        <Controller
          name="group"
          control={control}
          rules={{ required: 'Group is required' }}
          render={({ field }) => (
            <Select
              onValueChange={(val) => {
                const option = availableGroups.find(o => o.value === val);
                // ignore disabled options
                if (option && option.disabled) return;
                field.onChange(val);

              }}
              value={field.value || ''}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a group" />
              </SelectTrigger>
              <SelectContent>
                {availableGroups.map((option: SelectOption) => (
                  <SelectItem key={option.value} value={option.value} disabled={!!option.disabled}>
                    <span className={option.disabled ? 'text-gray-400' : undefined}>{option.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Logged In User Only */}
      <div>
        <div className="flex items-center space-x-2">
          <Controller
            name="currentUserOnly"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <Checkbox
                checked={field.value || false}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <Label forField="currentUserOnly" label="Logged in user data only" />
          <ErrorLabel error={errors.currentUserOnly?.message} />
        </div>
        <p className="text-sm text-gray-500 mt-1">
          If checked, the library item will only show data belonging to the currently logged in user.
        </p>
      </div>

      {/* Columns */}
      <div>
        <Label forField="visibleColumns" label={<>Columns to Display <span className="text-red-500">*</span></>} />
        <ErrorLabel error={errors.visibleColumns?.message} />
        <p className="text-sm text-gray-500 mt-1 mb-3">
          Which columns would you like to show for the library item?
        </p>
        <Controller
          name="visibleColumns"
          control={control}
          defaultValue={[]}
          rules={{
            required: 'At least one column is required',
            validate: (value) => {
              if (!value || value.length === 0) {
                return 'At least one column is required';
              }
              return true;
            }
          }}
          render={({ field }) => (
            <DropdownMultiSelect
              options={availableColumns}
              selected={field.value.map((fieldValue: string) => availableColumns.find(option => option.value === fieldValue)).filter(Boolean) as SelectOption[]}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions.map(option => option.value);
                field.onChange(selectedValues);
              }}
              allOptionInResults={false}
            />
          )}
        />
      </div>

      {/* Display By */}
      <div>
        <Label forField="displayBy" label="Display By" />
        <ErrorLabel error={errors.displayBy?.message} />
        <p className="text-sm text-gray-500 mt-1 mb-3">
          How would you like to group and display the data?
        </p>
        <Controller
          name="displayBy"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value || ''}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select display by option" />
              </SelectTrigger>
              <SelectContent>
                {getDisplayByOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Display Style */}
      <div>
        <Label forField="displayStyle" label="Display Style" />
        <ErrorLabel error={errors.displayStyle?.message} />
        <p className="text-sm text-gray-500 mt-1 mb-3">
          How would you like to display the data? Funnel is only available when Display By is set to Pipeline Stage.
        </p>
        <Controller
          name="displayStyle"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value || ''}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select display style" />
              </SelectTrigger>
              <SelectContent>
                {getDisplayStyleOptions().map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Theme - only show when display style is funnel */}
      {displayStyle === DisplayStyleType[DisplayStyleType.dsFunnelChart] && (
        <div>
          <Label forField="theme" label="Chart Theme" />
          <ErrorLabel error={errors.theme?.message} />
          <p className="text-sm text-gray-500 mt-1 mb-3">
            What color scheme would you like to use for the funnel chart?
          </p>
          <Controller
            name="theme"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={field.value || 'spectral'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  {getThemeOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      )}

      {/* Sort By - only show when display style is grid */}
      {displayStyle === DisplayStyleType[DisplayStyleType.dsGrid] && (
        <div>
          <Label forField="sortColumn" label="Sort By" />
          <ErrorLabel error={errors.sortColumn?.message} />
          <p className="text-sm text-gray-500 mt-1 mb-3">
            How would you like to sort the data?
          </p>
          <Controller
            name="sortColumn"
            control={control}
            render={({ field }) => (
              <Select
                onValueChange={field.onChange}
                value={field.value || ''}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select sort option" />
                </SelectTrigger>
                <SelectContent>
                  {getSortColumnOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />

          {/* Sort Descending checkbox */}
          <div className="flex items-center space-x-2 mt-3">
            <Controller
              name="sortDesc"
              control={control}
              render={({ field }) => (
                <Checkbox
                  id="sortDesc"
                  checked={field.value || false}
                  onCheckedChange={(checked) => field.onChange(!!checked)}
                />
              )}
            />
            <Label forField="sortDesc" label="Sort Descending" />
          </div>
        </div>
      )}

      {/* No. Result - only show when Display Style is Grid */}
      {displayStyle === DisplayStyleType[DisplayStyleType.dsGrid] && (
        <div>
          <Label forField="noResult" label="No. Result" />
          <ErrorLabel error={errors.noResult?.message} />
          <p className="text-sm text-gray-500 mt-1 mb-3">
            How many rows would you like to display in the grid view?
          </p>
          <Input
            inErrorState={!!errors.noResult}
            type="number"
            {...register('noResult', {
              required: displayStyle === DisplayStyleType[DisplayStyleType.dsGrid] ? 'Number of results is required for grid display' : false,
              min: { value: 1, message: 'Minimum value is 1' },
              max: { value: 100, message: 'Maximum value is 100' },
              valueAsNumber: true
            })}
            placeholder="Enter number of results (1-100)"
            min="1"
            max="100"
          />
        </div>
      )}

      {/* Save Dates As */}
      <div>
        <Label forField="dateFilterMode" label="Save Dates As" />
        <ErrorLabel error={errors.dateFilterMode?.message} />
        <p className="text-sm text-gray-500 mt-1 mb-3">
          How should dates be saved and calculated for this library item?
        </p>
        <Controller
          name="dateFilterMode"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={field.onChange}
              value={field.value || ''}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select date saving option" />
              </SelectTrigger>
              <SelectContent>
                {availableDateFilterModes.map((option: SelectOption) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {typeof dateFilterMode !== 'undefined' && generateDateExplanation(dateFilterMode) && (
          <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm text-gray-800">
              {availableDateFilterModes.find(opt => opt.value === dateFilterMode)?.label || dateFilterMode}:
              {' '}
              {generateDateExplanation(dateFilterMode)}
            </p>
          </div>
        )}
      </div>

      {/* Dynamic Filters Section */}
      <div>
        <DynamicFiltersSection
          hasValidationError={hasValidationError}
          onValidationErrorHandled={onValidationErrorHandled}
          availableFilters={availableFilters}
          multiSelectSelectedOptions={multiSelectSelectedOptions}
          onMultiSelectSelectedOptionsChange={handleMultiSelectSelectedOptionsChange}
        />
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t">
        <Button type="button" variant="outline" onClick={onCancel} className='text-kPurple-500'>
          Cancel
        </Button>
        <Button type="submit" className='bg-kPurple-500 hover:bg-kPurple-700 text-white hover:text-white font-bold'>
          {libraryItemId ? 'Update' : 'Create'}
          <FontAwesomeIcon icon={libraryItemId ? faCheck : faPlus} />
        </Button>
      </div>
    </div>
  );
};
